import { PenSquare, Trash2, Zap, FileText, Package } from "lucide-react";
import { Button } from "@/components/ui/button";

const InventoryTable = ({ items, canEdit, canDelete, onViewNotes, onViewImage, onViewLedger, onQuickEdit, onEdit, onDelete }) => {
  return (
    <div className="bg-white border border-slate-200 rounded-sm">
      <div className="overflow-x-auto">
        {items.length === 0 ? (
          <div className="p-12 text-center">
            <Package className="mx-auto text-slate-300 mb-4" size={64} />
            <p className="text-slate-500 text-lg">No items found</p>
          </div>
        ) : (
          <table className="w-full data-table" data-testid="inventory-table">
            <thead className="bg-slate-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Part Number</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Name</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Brand</th>
                <th className="text-left px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Location</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Weight (kg)</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Quantity</th>
                <th className="text-right px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Status</th>
                {(canEdit || canDelete) && (
                  <th className="text-center px-4 py-3 text-xs font-bold text-slate-600 uppercase tracking-wider">Actions</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((item) => {
                const isLowStock = item.quantity <= item.reorder_level;
                const hasNotes = item.notes && item.notes.trim().length > 0;
                const hasImage = item.image_url && item.image_url.trim().length > 0;
                return (
                  <tr key={item.id} className="hover:bg-orange-50/50 transition-colors" data-testid={`inventory-row-${item.part_number}`}>
                    <td className="px-4 py-4 text-sm font-medium text-slate-900">
                      <div className="flex items-center gap-2">
                        {item.part_number}
                        {hasNotes && (
                          <button onClick={() => onViewNotes(item)} className="text-orange-500 hover:text-orange-600 cursor-pointer" title="View notes">
                            <FileText size={14} />
                          </button>
                        )}
                        {hasImage && (
                          <button onClick={() => onViewImage(item)} className="text-blue-500 hover:text-blue-600 cursor-pointer" title="View image">
                            <Package size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 text-sm text-slate-700">{item.name}</td>
                    <td className="px-4 py-4 text-sm text-slate-600">{item.brand || '-'}</td>
                    <td className="px-4 py-4 text-sm text-slate-600">{item.location || '-'}</td>
                    <td className="px-4 py-4 text-sm text-right text-slate-600">{item.weight ? item.weight.toFixed(1) : '-'}</td>
                    <td className="px-4 py-4 text-sm text-right font-semibold">
                      <span className={isLowStock ? "text-orange-500" : "text-slate-900"}>{item.quantity}</span>
                    </td>
                    <td className="px-4 py-4 text-right">
                      {isLowStock ? (
                        <span className="inline-block px-2 py-1 bg-orange-100 text-orange-700 text-xs font-bold uppercase rounded-sm">Low Stock</span>
                      ) : (
                        <span className="inline-block px-2 py-1 bg-emerald-100 text-emerald-700 text-xs font-bold uppercase rounded-sm">In Stock</span>
                      )}
                    </td>
                    {(canEdit || canDelete) && (
                      <td className="px-4 py-4">
                        <div className="flex items-center justify-center gap-1">
                          <Button data-testid={`ledger-${item.part_number}`} size="sm" variant="ghost" onClick={() => onViewLedger(item)} className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-sm btn-snap" title="View Ledger">
                            <FileText size={16} strokeWidth={2} />
                          </Button>
                          {canEdit && (
                            <>
                              <Button data-testid={`quick-edit-${item.part_number}`} size="sm" variant="ghost" onClick={() => onQuickEdit(item)} className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-sm btn-snap">
                                <Zap size={16} strokeWidth={2} />
                              </Button>
                              <Button data-testid={`edit-item-${item.part_number}`} size="sm" variant="ghost" onClick={() => onEdit(item)} className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-sm btn-snap">
                                <PenSquare size={16} strokeWidth={2} />
                              </Button>
                            </>
                          )}
                          {canDelete && (
                            <Button data-testid={`delete-item-${item.part_number}`} size="sm" variant="ghost" onClick={() => onDelete(item.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-sm btn-snap">
                              <Trash2 size={16} strokeWidth={2} />
                            </Button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export { InventoryTable };
